import { useCallback, useEffect, useRef, useState } from "react";
import { education, experience, profile, projects, stackLayers } from "./data/content";
import "./App.css";

const Arrow = () => <span aria-hidden="true">↗</span>;

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll("[data-reveal]");
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (entry.isIntersecting) { entry.target.classList.add("revealed"); observer.unobserve(entry.target); }
    }), { threshold: 0.12 });
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

function MagneticLink({ className = "", children, ...props }) {
  const ref = useRef(null);
  const move = useCallback((event) => {
    if (window.matchMedia("(pointer: coarse)").matches) return;
    const rect = ref.current.getBoundingClientRect();
    ref.current.style.transform = `translate(${(event.clientX - rect.left - rect.width / 2) * 0.16}px, ${(event.clientY - rect.top - rect.height / 2) * 0.16}px)`;
  }, []);
  return <a ref={ref} className={`magnetic ${className}`} onMouseMove={move} onMouseLeave={() => { ref.current.style.transform = ""; }} {...props}>{children}</a>;
}

function Navigation({ onPalette }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("work");
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => entry.isIntersecting && setActive(entry.target.id)), { rootMargin: "-35% 0px -55%" });
    document.querySelectorAll("main section[id]").forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);
  const links = [["work", "Work"], ["experience", "Experience"], ["about", "About"], ["education", "Education"]];
  return <header className="nav-shell"><nav className="nav" aria-label="Main navigation">
    <a className="brand" href="#top" aria-label="Back to top">KC<span>.</span></a>
    <div className={`nav-items ${open ? "open" : ""}`}>{links.map(([id, label]) => <a key={id} className={active === id ? "active" : ""} href={`#${id}`} onClick={() => setOpen(false)}><i />{label}</a>)}</div>
    <button className="command-trigger" onClick={onPalette} aria-label="Open command menu">⌘ K</button>
    <a className="nav-cta" href={`mailto:${profile.email}`}>Contact <Arrow /></a>
    <button className="menu-button" onClick={() => setOpen(!open)} aria-expanded={open} aria-label="Toggle menu"><span /><span /></button>
  </nav></header>;
}

function PortraitStage() {
  const [hasPortrait, setHasPortrait] = useState(true);
  return <div className="portrait-stage" aria-label="Portrait area">
    <div className="portrait-glow" aria-hidden="true" />
    <div className="portrait-grid" aria-hidden="true" />
    <div className="portrait-note" aria-hidden={hasPortrait}>
      <span>PORTRAIT SPACE</span>
      <small>Add your transparent PNG as<br /><b>public/portrait.png</b></small>
    </div>
    <img className="portrait-image" src="/portrait.png" alt="Sai Krishna Chaitanya Lanka" onError={() => setHasPortrait(false)} style={{ display: hasPortrait ? "block" : "none" }} />
    <div className="portrait-chip chip-one">FULL STACK</div>
    <div className="portrait-chip chip-two">MOBILE + AI</div>
    <div className="portrait-status"><span /> OPEN TO OPPORTUNITIES</div>
  </div>;
}

function Hero() {
  return <section className="hero" id="top">
    <div className="hero-copy">
      <p className="eyebrow"><span className="status-dot" /> SOFTWARE ENGINEER / HYDERABAD, INDIA</p>
      <p className="hero-name"><strong>Sai Krishna Chaitanya Lanka</strong></p>
      <h1 aria-label="I am a full stack developer"><span>I AM A</span><span><em>FULL STACK</em></span><span><em>DEVELOPER.</em></span></h1>
      <p className="hero-description">I build web apps, mobile apps, APIs, and backend systems.</p>
      <div className="hero-actions"><MagneticLink className="primary-action" href="#work">View selected work <span>↓</span></MagneticLink><MagneticLink className="text-action" href={`mailto:${profile.email}?subject=Resume request`}>Request résumé <Arrow /></MagneticLink></div>
      <div className="tech-ticker" aria-label="Core technologies">{["React", "React Native", "Node", ".NET", "Postgres", "AI"].map((tech) => <span key={tech}>{tech}</span>)}</div>
    </div>
    <PortraitStage />
    <div className="hero-foot"><span>Open to developer opportunities</span><span>SCROLL / 01 TO 06</span></div>
  </section>;
}


function SectionHeading({ number, eyebrow, children }) { return <div className="section-heading" data-reveal><p>{number} / {eyebrow}</p><h2>{children}</h2></div>; }
function Project({ project }) {
  return <article className={`project project-${project.tone}`} id={project.id} data-reveal>
    <div className="project-index"><span>{project.number}</span><p>{project.category}</p></div>
    <div className="project-main">
      <div className="project-title-row"><h3>{project.title}</h3><div className="project-mark">{project.number}</div></div>
      <div className="project-meta"><div><small>SCOPE</small><span>{project.systems}</span></div><div><small>PERIOD</small><span>{project.period}</span></div></div>
      <p className="project-summary">{project.summary}</p>
      <div className="project-metrics" aria-label="Project numbers">{project.metrics.map((metric) => <div key={metric.label}><strong>{metric.value}</strong><span>{metric.label}</span></div>)}</div>
      <div className="project-bottom"><div>{project.stack.map((item) => <span key={item}>{item}</span>)}</div></div>
    </div>
  </article>;
}

function StackMap() {
  const [active, setActive] = useState(0);
  return <section className="section stack-section" id="stack"><SectionHeading number="02" eyebrow="CAPABILITIES">CORE <em>STACK</em></SectionHeading>
    <div className="stack-map" data-reveal><div className="stack-rail" aria-hidden="true"><span style={{ transform: `translateY(${active * 100}%)` }} /></div>{stackLayers.map((layer, index) => <button key={layer.name} className={`stack-layer ${active === index ? "active" : ""}`} onMouseEnter={() => setActive(index)} onFocus={() => setActive(index)} onClick={() => setActive(index)}><small>{layer.code}</small><h3>{layer.name}</h3><div>{layer.items.map((item) => <span key={item}>{item}</span>)}</div><b>+</b></button>)}</div>
  </section>;
}

function Experience() {
  return <section className="section experience" id="experience"><div className="experience-label"><p>03 / EXPERIENCE</p><h2>PROFESSIONAL<br /><em>EXPERIENCE</em></h2></div><div className="experience-content" data-reveal>
    <div className="job-head"><div><small>COMPANY</small><h3>{experience.company}</h3></div><div><small>PERIOD</small><span>{experience.period}</span></div></div>
    <p className="job-role">{experience.role} · {experience.location}</p><blockquote>{experience.statement}</blockquote>
    <ol>{experience.points.map((point, i) => <li key={point}><span>0{i + 1}</span><p>{point}</p></li>)}</ol>
  </div></section>;
}

function About() {
  return <section className="section about" id="about"><div className="about-main" data-reveal><p>04 / ABOUT</p><h2>A BIT<br /><em>ABOUT ME.</em></h2></div><div className="about-copy" data-reveal><p>{profile.summary}</p></div></section>;
}

function Education() {
  return <section className="section education-section" id="education">
    <SectionHeading number="05" eyebrow="EDUCATION">ACADEMIC <em>BACKGROUND</em></SectionHeading>
    <div className="education-card" data-reveal>
      <div><small>DEGREE</small><h3>{education.degree}</h3></div>
      <div><small>INSTITUTION</small><p>{education.school}</p></div>
      <div><small>GRADUATED</small><p>{education.year}</p></div>
    </div>
  </section>;
}

function Contact() {
  return <section className="contact" id="contact"><p>06 / CONTACT · HYDERABAD, INDIA</p><h2>HAVE A PROJECT?<br /><em>LET’S TALK.</em></h2><MagneticLink className="contact-link" href={`mailto:${profile.email}`}>GET IN TOUCH <Arrow /></MagneticLink><div className="contact-meta"><a href={`mailto:${profile.email}`}>Email</a><a href={profile.linkedin} target="_blank" rel="noreferrer">LinkedIn</a><a href={profile.github} target="_blank" rel="noreferrer">GitHub</a></div><div className="footer-word" aria-hidden="true">KRRISH</div></section>;
}

function CommandPalette({ open, onClose }) {
  useEffect(() => {
    const handler = (event) => { if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") { event.preventDefault(); open ? onClose() : document.querySelector(".command-trigger")?.click(); } if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler); return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);
  if (!open) return null;
  const actions = [["About me", "#about"], ["View projects", "#work"], ["Education", "#education"], ["Open GitHub", profile.github], ["Open LinkedIn", profile.linkedin], ["Request résumé", `mailto:${profile.email}?subject=Resume request`], ["Contact me", `mailto:${profile.email}`]];
  return <div className="palette-backdrop" onMouseDown={onClose}><div className="palette" role="dialog" aria-modal="true" aria-label="Quick navigation" onMouseDown={(e) => e.stopPropagation()}><div className="palette-head"><span>QUICK SWITCHER</span><button onClick={onClose}>ESC</button></div>{actions.map(([label, href], i) => <a key={label} href={href} onClick={onClose}><small>0{i + 1}</small>{label}<Arrow /></a>)}</div></div>;
}

function Cursor() {
  const ref = useRef(null);
  useEffect(() => { const move = (e) => { ref.current.style.transform = `translate3d(${e.clientX}px, ${e.clientY}px, 0)`; }; window.addEventListener("pointermove", move); return () => window.removeEventListener("pointermove", move); }, []);
  return <div className="cursor" ref={ref} aria-hidden="true" />;
}

export default function App() {
  const [palette, setPalette] = useState(false); const [loaded, setLoaded] = useState(false); useReveal();
  useEffect(() => { const timer = setTimeout(() => setLoaded(true), 720); return () => clearTimeout(timer); }, []);
  return <div className={loaded ? "app loaded" : "app"}><a className="skip-link" href="#work">Skip to selected work</a><div className="intro" aria-hidden="true"><span>KRRISH</span></div><Cursor /><Navigation onPalette={() => setPalette(true)} /><main><Hero /><section className="section work" id="work"><SectionHeading number="01" eyebrow="SELECTED WORK">SELECTED<br /><em>PROJECTS</em></SectionHeading>{projects.map((project) => <Project key={project.id} project={project} />)}</section><StackMap /><Experience /><About /><Education /><Contact /></main><CommandPalette open={palette} onClose={() => setPalette(false)} /></div>;
}
